import { PsHttpService, type PatSnapSuccessResponseData } from '@patsnap/tahiti-core';
import type { FtoReportCreateTaskApi, CreateNoveltyCheckTaskRequest, CreateNoveltyCheckTaskResponse } from '../create-novelty-check-task-api.ts';
import { coreTelecomApiUrlPrefix } from '@telecom/shared/domain-config';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { FtoReportFeatureRecognizeApi, FeatureRecognizeRequest, FeatureRecognizeResponse } from '../feature-recognize-api.ts';
import type { FtoReportInputSummaryApi, SummarizeNoveltyInputRequest, SummarizeNoveltyInputResponse } from '../novelty-input-summary-api.ts';
import type { FtoReportSearchApi, SearchNoveltyRequest, SearchNoveltyResponse } from '../search-novelty-api.ts';
import type { SearchNoveltyResultResponse, FtoReportResultSearchApi, SearchNoveltyResultRequest } from '../search-novelty-result-api.ts';
import type { FtoReportTechnicalFeaturesGetApi, TechnicalFeaturesGetApiRequest, TechnicalFeaturesGetApiResponse } from '../technical-features-get-api.ts';
import type { FtoReportTechFeatureSubmitApi, TechFeatureSubmitApiRequest, TechFeatureSubmitApiResponse } from '../tech-feature-submit-api.ts';
import type { FtoReportTitleApi, NoveltyCheckReportTitleApiRequest, NoveltyCheckReportTitleApiResponse } from '../novelty-check-report-title-api.ts';
import type { FtoReportTechEssentialUpdateApi, TechEssentialUpdateApiRequest, TechEssentialUpdateApiResponse } from '../tech-essential-update-api.ts';
import type { FtoReportSubtitleInfoApi, NoveltyReportSubtitleInfoRequest, NoveltyReportSubtitleInfoResponse } from '../novelty-report-subtitle-info-api.ts';
import type { FtoReportConclusionApi, FtoReportConclusionRequest, FtoReportConclusionResponse } from '../comparison-public-situation-api.ts';
import type { FtoReportUpdateSimilarListApi, UpdateNoveltySimilarListRequest, UpdateNoveltySimilarListResponse } from '../update-novelty-similar-list-api.ts';
import type { FtoReportTerminateSearchAgentApi, TerminateSearchAgentApiRequest, TerminateSearchAgentApiResponse } from '../terminate-search-agent-api.ts';
import type { FtoReportFeatureComparisonApi, NoveltyFeatureComparisonRequest, NoveltyFeatureComparisonResponse } from '../novelty-feature-comparison-api.ts';
import type { FtoReportInputLanguageDetectApi, NoveltyInputLanguageDetectRequest, NoveltyInputLanguageDetectResponse } from '../novelty-input-language-detect-api.ts';
import type { FtoReportImportPatentsApi, GetImportProgressRequest, GetImportProgressResponse, StartImportPatentsRequest, StartImportPatentsResponse } from '../import-patents-api.ts';

@injectable()
export class FtoReportApiServiceImpl extends PsHttpService
    implements
        FtoReportInputLanguageDetectApi,
        FtoReportTerminateSearchAgentApi,
        FtoReportConclusionApi,
        FtoReportSubtitleInfoApi,
        FtoReportTechEssentialUpdateApi,
        FtoReportTitleApi,
        FtoReportTechFeatureSubmitApi,
        FtoReportTechnicalFeaturesGetApi,
        FtoReportResultSearchApi,
        FtoReportSearchApi,
        FtoReportInputSummaryApi,
        FtoReportFeatureRecognizeApi,
        FtoReportCreateTaskApi,
        FtoReportUpdateSimilarListApi,
        FtoReportFeatureComparisonApi,
        FtoReportImportPatentsApi
{
    async detect(data: NoveltyInputLanguageDetectRequest): Promise<NoveltyInputLanguageDetectResponse> {
        const res = await this.post<PatSnapSuccessResponseData<NoveltyInputLanguageDetectResponse>>({
            url: 'ai/task/lang-detect',
            data,
        });
        return res.data;
    }


    async terminateSearchAgent(params: TerminateSearchAgentApiRequest): Promise<TerminateSearchAgentApiResponse> {
        const res = await this.post<PatSnapSuccessResponseData<TerminateSearchAgentApiResponse>>({
            url: 'fto-search/agent/terminate',
            params,
        });
        return res.data;
    }

    async getNoveltyReportConclusion(params: FtoReportConclusionRequest): Promise<FtoReportConclusionResponse> {
        const res = await this.get<PatSnapSuccessResponseData<FtoReportConclusionResponse>>({
            url: `fto-search/report/result/${params.task_id}`,
        });
        return res.data;
    }


    async getNoveltyReportSubtitleInfo(params: NoveltyReportSubtitleInfoRequest): Promise<NoveltyReportSubtitleInfoResponse> {
        const res = await this.get<PatSnapSuccessResponseData<NoveltyReportSubtitleInfoResponse>>({
            url: `fto-search/report/title/${params.task_id}`,
        });
        return res.data;
    }

    async updateTechSummary(data: TechEssentialUpdateApiRequest): Promise<TechEssentialUpdateApiResponse> {
        const res = await this.post<PatSnapSuccessResponseData<TechEssentialUpdateApiResponse>>({
            url: 'fto-search/update-tech-essential',
            data,
        });
        return res.data;
    }

    async getNoveltyCheckReportTitle(data: NoveltyCheckReportTitleApiRequest): Promise<NoveltyCheckReportTitleApiResponse> {
        const res = await this.post<PatSnapSuccessResponseData<NoveltyCheckReportTitleApiResponse>>({
            url: 'fto-search/title',
            data,
        });
        return res.data;
    }

    async submitTechFeature(data: TechFeatureSubmitApiRequest): Promise<TechFeatureSubmitApiResponse> {
        const res = await this.post<PatSnapSuccessResponseData<TechFeatureSubmitApiResponse>>({
            url: 'fto-search/confirm-tech-feature',
            data,
        });
        return res.data;
    }

    async getTechnicalFeatures(data: TechnicalFeaturesGetApiRequest, signal?: AbortSignal): Promise<TechnicalFeaturesGetApiResponse> {
        const res = await this.get<PatSnapSuccessResponseData<TechnicalFeaturesGetApiResponse>>({
            url: `fto-search/get-tech-essential-feature/${data.task_id}`,
            signal,
        });
        return res.data;
    }

    async getSearchNoveltyResult(params: SearchNoveltyResultRequest, signal?: AbortSignal): Promise<SearchNoveltyResultResponse> {
        const res = await this.get<PatSnapSuccessResponseData<SearchNoveltyResultResponse>>({
            url: 'fto-search/agent/result',
            params,
            signal,
        });
        return res.data;
    }

    async startSearchNovelty(data: SearchNoveltyRequest): Promise<SearchNoveltyResponse> {
        const res = await this.post<PatSnapSuccessResponseData<SearchNoveltyResponse>>({
            url: 'fto-search/agent',
            data,
        });
        return res.data;
    }

    async summarizeNoveltyInput(data: SummarizeNoveltyInputRequest): Promise<SummarizeNoveltyInputResponse> {
        const res = await this.post<PatSnapSuccessResponseData<SummarizeNoveltyInputResponse>>({
            url: 'fto-search/text-summary',
            data,
        });
        return res.data;
    }

    async recognize(data: FeatureRecognizeRequest): Promise<FeatureRecognizeResponse> {
        const res = await this.post<PatSnapSuccessResponseData<FeatureRecognizeResponse>>({
            url: 'fto-search/feature-extract',
            data,
        });
        return res.data;
    }

    async createNoveltyCheckTask(data: CreateNoveltyCheckTaskRequest): Promise<CreateNoveltyCheckTaskResponse> {
        const res = await this.post<PatSnapSuccessResponseData<CreateNoveltyCheckTaskResponse>>({
            url: 'fto-search/submit',
            data,
        });
        return res.data;
    }

    async updateNoveltySimilarList(data: UpdateNoveltySimilarListRequest): Promise<UpdateNoveltySimilarListResponse> {
        const res = await this.post<PatSnapSuccessResponseData<UpdateNoveltySimilarListResponse>>({
            url: 'fto-search/update-similar-list',
            data,
        });
        return res.data;
    }

    async getFeatureComparison(data: NoveltyFeatureComparisonRequest): Promise<NoveltyFeatureComparisonResponse> {
        const res = await this.post<PatSnapSuccessResponseData<NoveltyFeatureComparisonResponse>>({
            url: 'fto-search/feature-comparison',
            data,
        });
        return res.data;
    }

    async startImportPatents(data: StartImportPatentsRequest, signal?: AbortSignal): Promise<StartImportPatentsResponse> {
        const res = await this.post<PatSnapSuccessResponseData<StartImportPatentsResponse>>({
            url: 'fto-search/folder-feature-comparison/start',
            data,
            signal,
        });
        return res.data;
    }

    async getImportProgress(data: GetImportProgressRequest, signal?: AbortSignal): Promise<GetImportProgressResponse> {
        const res = await this.get<PatSnapSuccessResponseData<GetImportProgressResponse>>({
            url: `fto-search/folder-feature-comparison/progress/${data.async_id}`,
            signal,
        });
        return res.data;
    }

    get options() {
        return {
            baseURL: coreTelecomApiUrlPrefix,
            headers: {
                'X-API-Version': '2.0',
            },
        };
    }
}
